import React, { useState, useEffect } from 'react'
import { 
  FaCalendarAlt, 
  FaCheck, 
  FaTimes, 
  FaUsers, 
  FaSearch,
  FaEye,
  FaChartBar,
  FaUserCheck,
  FaUserTimes,
  FaClock,
  FaCalendarDay
} from 'react-icons/fa'
import { useAuth } from '../../context/Auth'
import { toast } from 'react-toastify'

const ChurchAttendance = () => {
  const { requestWithAuth } = useAuth()
  const [members, setMembers] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [attendance, setAttendance] = useState({})
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [attendanceHistory, setAttendanceHistory] = useState({})
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchGroupMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await requestWithAuth(
        `/members/?status=${statusFilter}&search=${debouncedSearch}&paginated=false`,
        "GET"
      );
      setMembers(response.data?.members || []);
    } catch (error) {
      toast.error("Failed to load church members");
    }finally{
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchGroupMembers();
  }, [debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Initialize with current or next Sunday
  useEffect(() => {
    const today = new Date()
    const currentDay = today.getDay()
    const daysUntilSunday = currentDay === 0 ? 0 : 7 - currentDay
    const nextSunday = new Date(today)
    nextSunday.setDate(today.getDate() + daysUntilSunday)
    
    setSelectedDate(nextSunday.toISOString().split('T')[0])
  }, [])

  

  // Load attendance for selected date
  useEffect(() => {
    if (selectedDate) {
      loadAttendanceForDate(selectedDate)
    }
  }, [selectedDate])

  const loadAttendanceForDate = async (date) => {
    try {
      setLoading(true);
      const response = await requestWithAuth(
        `/members/church/attendance/?date=${date}`,
        "GET"
      );
      setAttendance(response.data);
    } catch (error) {
      toast.error("Failed to load attendance");
    }finally{
      setLoading(false);
    }

  }

  const markAttendance = (memberId, status) => {
    let currentTime = null;

    if (status === 'present') {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0"); 
      const minutes = String(now.getMinutes()).padStart(2, "0");
      currentTime = `${hours}:${minutes}`; 
    }
    setAttendance(prev => ({
      ...prev,
      [memberId]: {
        status,
        time: status === 'present' ? currentTime : null
      }
    }))
  }

  const saveAttendance = async () => {
    try {
      setLoading(true);
      await requestWithAuth(
        `/members/church/attendance/?date=${selectedDate}`,
        "POST", attendance, {
          "Content-Type": "application/json"
        }
      );
      toast.success("Attendance saved successfully");
    } catch (error) {
      toast.error("Failed to save attendance");
    }finally{
      setLoading(false);
    }
  }

  const getAttendanceStats = () => {
    const total = members?.length
    const present = members?.filter(member => attendance[member.id]?.status === 'present').length
    const absent = members?.filter(member => attendance[member.id]?.status === 'absent').length
    const unmarked = total - present - absent

    
    return { total, present, absent, unmarked }
  }

  const stats = getAttendanceStats()

  const getStatusButton = (memberId, status) => {
    const currentStatus = attendance[memberId]?.status
    const isActive = currentStatus === status
    
    const baseClasses = "px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200"
    const statusConfig = {
      present: {
        classes: isActive ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-green-100',
        icon: <FaCheck />
      },
      absent: {
        classes: isActive ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-red-100',
        icon: <FaTimes />
      }
    }

    return (
      <button
        onClick={() => markAttendance(memberId, status)}
        className={`${baseClasses} ${statusConfig[status].classes} flex items-center gap-1`}
      >
        {statusConfig[status].icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </button>
    )
  }

  const isSelectedDateSunday = () => {
    if (!selectedDate) return false
    const date = new Date(selectedDate)
    return date.getDay() === 0
  }

  const getNextSundays = () => {
    const sundays = []
    const today = new Date()
    
    // Get last 4 Sundays and next 4 Sundays
    for (let i = -5; i <= 0; i++) {
      const date = new Date(today)
      const currentDay = today.getDay()
      const daysToSunday = currentDay === 0 ? 0 : 7 - currentDay
      date.setDate(today.getDate() + daysToSunday + (i * 7))
      
      sundays.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        isPast: date < today,
        isToday: date.toDateString() === today.toDateString()
      })
    }
    
    return sundays
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaCalendarAlt className="text-2xl text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Sunday Attendance</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={saveAttendance}
                disabled={loading || Object.keys(attendance).length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {loading ? 'Saving...' : 'Save Attendance'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Date Selection & Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Sunday</h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaCalendarDay />
                  Weekly attendance is recorded every Sunday
                </div>
              </div>
              
              {/* Quick Sunday Selection */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2 mb-4">
                {getNextSundays().map((sunday) => (
                  <button
                    key={sunday.date}
                    onClick={() => setSelectedDate(sunday.date)}
                    className={`text-left p-3 rounded-lg border transition-all text-sm ${
                      selectedDate === sunday.date
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : sunday.isPast
                        ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">
                      {sunday.date === new Date().toISOString().split('T')[0] ? 'Today' : 
                       sunday.label.split(',')[0]}
                    </div>
                    <div className="text-xs opacity-75">
                      {sunday.label.split(',')[1]?.trim()}
                    </div>
                    
                  </button>
                ))}
              </div>

              {/* Custom Date Input */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">Or select custom date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {selectedDate && !isSelectedDateSunday() && (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    ⚠️ Selected date is not a Sunday
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Panel */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Members</span>
                <span className="font-semibold">{stats.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <FaUserCheck /> Present
                </span>
                <span className="font-semibold text-green-600">{stats.present}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-600 flex items-center gap-1">
                  <FaUserTimes /> Absent
                </span>
                <span className="font-semibold text-red-600">{stats.absent}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Unmarked</span>
                <span className="font-semibold text-gray-500">{stats.unmarked}</span>
              </div>
              
              {stats.total > 0 && (
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Attendance Rate</span>
                    <span className="font-semibold text-blue-600">
                      {Math.round((stats.present / (stats.present + stats.absent)) * 100) || 0}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="text-sm text-gray-600">
              {selectedDate && (
                <span>
                  Recording attendance for: <strong>{new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</strong>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Attendance Marking */}
        {selectedDate && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Mark Attendance
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {new Date(selectedDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                {attendance[selectedDate] && (
                  <span className="ml-2 text-green-600">• Previously recorded</span>
                )}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading attendance data...</p>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-12">
                <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
                <p className="text-gray-500">Try adjusting your search terms</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Attendance Actions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time Marked
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members?.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {member?.full_name}
                            </div>
                            <div className="text-sm text-gray-500">{member?.member_church_id}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            member?.status == "Active"
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {member?.status == "Active" ? <FaUserCheck /> : <FaUserTimes />}
                            {member?.status == "Active" ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getStatusButton(member.id, 'present')}
                            {getStatusButton(member.id, 'absent')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {attendance[member.id]?.time || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ChurchAttendance